import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import RadioGroup from 'tio-ui/components/radio-group';
import Form from 'tio-ui/components/form';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import * as v from 'valibot';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import { addEmployeeDependentRequest } from 'tio-data/requests/employee-actions';

interface InviteFamilyMemberModalSignature {
  Args: {
    show: boolean;
    employee: EmployeeModel;
    onClose: () => void;
    onSuccess: () => void;
  };
}

const RELATIONSHIP_OPTIONS = [
  { key: 'CHILD', label: 'profile.family.modal.relationship_child' },
  { key: 'SPOUSE', label: 'profile.family.modal.relationship_spouse' },
  { key: 'PARTNER', label: 'profile.family.modal.relationship_partner' },
];

type ModalStep = 'form' | 'terms' | 'success';

type InviteFormData = {
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
  relationship: string;
};

const formSchema = v.object({
  firstName: v.pipe(v.string('First Name is Required'), v.nonEmpty('First Name is Required')),
  lastName: v.pipe(v.string('Last Name is Required'), v.nonEmpty('Last Name is Required')),
  email: v.pipe(v.string('Invalid Email Address'), v.email('Invalid Email Address')),
  birthDate: v.pipe(v.string('Birth Date is Required'), v.nonEmpty('Birth Date is Required')),
  relationship: v.pipe(
    v.string('Relationship is Required'),
    v.nonEmpty('Relationship is Required')
  ),
});

export default class InviteFamilyMemberModal extends Component<InviteFamilyMemberModalSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked formData: InviteFormData = {
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    relationship: '',
  };

  @tracked currentStep: ModalStep = 'form';
  @tracked isSubmitting = false;
  @tracked hasAttemptedSubmit = false;
  @tracked errorMessage = '';
  @tracked formValidationErrors: Record<string, string[]> = {};

  relationshipOptions = RELATIONSHIP_OPTIONS;

  resetForm = () => {
    this.formData = {
      firstName: '',
      lastName: '',
      email: '',
      birthDate: '',
      relationship: '',
    };
    this.errorMessage = '';
    this.isSubmitting = false;
    this.hasAttemptedSubmit = false;
    this.currentStep = 'form';
    this.formValidationErrors = {};
  };

  handleClose = () => {
    if (this.currentStep === 'success') {
      this.resetForm();
      this.args.onSuccess();
    } else {
      this.resetForm();
      this.args.onClose();
    }
  };

  validateField = (data: InviteFormData) => {
    const result = v.safeParse(formSchema, data);

    if (result.success) {
      this.formValidationErrors = {};
      return true;
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
      return false;
    }
  };

  onFieldChange = (field: keyof InviteFormData, value: string) => {
    this.formData = {
      ...this.formData,
      [field]: value,
    };
    if (this.hasAttemptedSubmit) {
      this.validateField(this.formData);
    }
  };

  onRelationshipChange = (value: string) => {
    this.formData = {
      ...this.formData,
      relationship: value,
    };
    if (this.hasAttemptedSubmit) {
      this.validateField(this.formData);
    }
  };

  handleFormSubmit = (_result: unknown, event?: SubmitEvent) => {
    event?.preventDefault();
    this.proceedToTerms();
  };

  proceedToTerms = () => {
    this.hasAttemptedSubmit = true;
    const isValid = this.validateField(this.formData);
    if (!isValid) return;
    this.currentStep = 'terms';
  };

  acceptTerms = async () => {
    this.isSubmitting = true;
    this.errorMessage = '';

    try {
      const employeeId = this.args.employee.id;

      if (!employeeId) {
        throw new Error('Employee ID is required');
      }

      // WarpDrive builder write (wave 1.5) — replaces the custom employee
      // adapter `addEmployeeDependent` method.
      // SINGULAR POST /employee/{id}/add-employee-dependent; snake_case body.
      await this.store.request(
        addEmployeeDependentRequest(employeeId, {
          first_name: this.formData.firstName,
          last_name: this.formData.lastName,
          email: this.formData.email,
          birth_date: this.formData.birthDate,
          relationship: this.formData.relationship,
        })
      );

      this.currentStep = 'success';
    } catch (error) {
      this.errorMessage = this.intl.t('profile.family.modal.error_message');
      this.currentStep = 'form'; // Go back to form on error
      console.error('Error inviting family member:', error);
    } finally {
      this.isSubmitting = false;
    }
  };

  declineTerms = () => {
    this.currentStep = 'form';
  };

  <template>
    <Modal @isOpen={{@show}} @onClose={{this.handleClose}} @size="lg">
      <:header>
        {{#if (eq this.currentStep "form")}}
          {{! Step 1: Contact Form }}
          <div class="text-center">
            <h2 class="tio-text-2xl-semibold tio-fg-brand">{{t "profile.family.modal.title"}}</h2>
            <p class="tio-fg-secondary mt-2">{{t "profile.family.modal.subtitle"}}</p>
          </div>
        {{else if (eq this.currentStep "terms")}}
          {{! Step 2: Terms Acceptance }}
          {{t "profile.family.modal.terms_title"}}
        {{else if (eq this.currentStep "success")}}
          {{! Step 3: Success Message }}
          <h2 class="tio-text-2xl-semibold text-center">{{t
              "profile.family.modal.success_title"
            }}</h2>
        {{/if}}
      </:header>
      <:body>
        {{#if (eq this.currentStep "form")}}
          <Form @data={{this.formData}} @onSubmit={{this.handleFormSubmit}} class="space-y-4">
            {{#if this.errorMessage}}
              <div class="p-3 tio-bg-danger-soft border tio-border-danger rounded tio-fg-danger">
                {{this.errorMessage}}
              </div>
            {{/if}}

            {{! Two-column layout for name/email fields }}
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                @label={{t "profile.family.modal.first_name"}}
                @value={{this.formData.firstName}}
                @onChange={{fn this.onFieldChange "firstName"}}
                @errors={{errorsForValibotField "firstName" schemaErrors=this.formValidationErrors}}
                name="firstName"
                placeholder="Bessy"
                data-test-family-invite-first-name
                required
              />

              <Input
                @label={{t "profile.family.modal.last_name"}}
                @value={{this.formData.lastName}}
                @onChange={{fn this.onFieldChange "lastName"}}
                @errors={{errorsForValibotField "lastName" schemaErrors=this.formValidationErrors}}
                name="lastName"
                placeholder="Doe"
                data-test-family-invite-last-name
                required
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                @label={{t "profile.family.modal.email"}}
                @value={{this.formData.email}}
                @onChange={{fn this.onFieldChange "email"}}
                @errors={{errorsForValibotField "email" schemaErrors=this.formValidationErrors}}
                name="email"
                type="email"
                placeholder="Bessydoe@email.com"
                data-test-family-invite-email
                required
              />

              <Input
                @label={{t "profile.family.modal.birth_date"}}
                @value={{this.formData.birthDate}}
                @onChange={{fn this.onFieldChange "birthDate"}}
                @errors={{errorsForValibotField "birthDate" schemaErrors=this.formValidationErrors}}
                name="birthDate"
                type="date"
                data-test-family-invite-birth-date
                required
              />
            </div>

            {{! Radio button group for relationship }}
            <div class="mt-4">
              <RadioGroup
                @label={{t "profile.family.modal.relationship_question"}}
                @value={{this.formData.relationship}}
                @errors={{errorsForValibotField
                  "relationship"
                  schemaErrors=this.formValidationErrors
                }}
                @orientation="horizontal"
                @onChange={{this.onRelationshipChange}}
                @options={{array
                  (hash
                    key="CHILD"
                    label=(t "profile.family.modal.relationship_child")
                    dataTestId="radio-child"
                  )
                  (hash
                    key="SPOUSE"
                    label=(t "profile.family.modal.relationship_spouse")
                    dataTestId="radio-spouse"
                  )
                  (hash
                    key="PARTNER"
                    label=(t "profile.family.modal.relationship_partner")
                    dataTestId="radio-partner"
                  )
                }}
              />
            </div>
          </Form>
        {{else if (eq this.currentStep "terms")}}
          <div class="prose prose-sm max-w-none">
            <p>
              {{t "profile.family.modal.terms_description"}}
            </p>
            <ul>
              <li>{{t "profile.family.modal.terms_list_1"}}</li>
              <li>{{t "profile.family.modal.terms_list_2"}}</li>
              <li>{{t "profile.family.modal.terms_list_3" htmlSafe=true}}</li>
              <li>{{t "profile.family.modal.terms_list_4"}}</li>
              <li>{{t "profile.family.modal.terms_list_5"}}</li>
            </ul>
          </div>
        {{else if (eq this.currentStep "success")}}
          <div class="text-center">
            <p class="tio-fg-secondary mb-6">
              {{t
                "profile.family.modal.success_message"
                firstName=this.formData.firstName
                lastName=this.formData.lastName
              }}
            </p>
          </div>
        {{/if}}
      </:body>
      <:footer>
        {{#if (eq this.currentStep "form")}}
          <div class="tio-row-between w-full">
            <Button @appearance="minimal" @intent="primary" {{on "click" this.handleClose}}>
              {{t "profile.family.modal.cancel"}}
            </Button>
            <Button
              @intent="primary"
              data-test-family-invite-submit-button
              {{on "click" this.proceedToTerms}}
            >
              {{t "profile.family.modal.submit"}}
            </Button>
          </div>
        {{else if (eq this.currentStep "terms")}}
          <div class="tio-row-between w-full">
            <Button @appearance="minimal" @intent="primary" {{on "click" this.declineTerms}}>
              {{t "profile.family.modal.decline"}}
            </Button>
            <Button
              @intent="primary"
              @isRunning={{this.isSubmitting}}
              data-test-family-invite-accept-button
              {{on "click" this.acceptTerms}}
            >
              {{t "profile.family.modal.accept"}}
            </Button>
          </div>
        {{else if (eq this.currentStep "success")}}
          <div class="tio-flex-end w-full">
            <Button @intent="primary" {{on "click" this.handleClose}}>
              {{t "profile.family.modal.close"}}
            </Button>
          </div>
        {{/if}}
      </:footer>
    </Modal>
  </template>
}
